import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import CoreBadge from '@wix/da-ds/pkg/Badges/CoreBadge';
import { ZoneType, InstallableModuleType } from '../../../../../types/layout';
import { ModuleType } from '../../../../../types/modules';
import { WithSectionContextProps } from '../../../Sections/SectionContext';

import s from './WidgetTypeCard.scss';

export interface Props extends WithSectionContextProps, InstallableModuleType {
  zone: ZoneType;
  className?: string;
  hasCoreMembership?: boolean;
}

const WidgetTypeCard: React.FC<Props> = ({
  className,
  zone,
  limit,
  moduleName,
  restrict,
  installedCount = 0,
  hasCoreMembership,
}) => {
  const { t } = useTranslation();
  const coreLimit = restrict && restrict.priv === 'core' ? restrict.limit : 0;
  const isCoreOnlyModule = !!coreLimit;
  const isLocked =
    isCoreOnlyModule && !hasCoreMembership && installedCount >= limit;

  let title;
  let description;
  let miniatureClassname;
  let rootClassname;
  switch (moduleName) {
    case ModuleType.ABOUT:
      title = t('widgets.aboutMe.addButtonHeader');
      description = t('widgets.aboutMe.description');
      miniatureClassname = 'about';
      break;
    case ModuleType.EXHIBITIONIST:
      if (zone === ZoneType.TOP_BAR) {
        title = t('widgets.exhibitionist.scroller.addButtonHeader');
        description = t('widgets.exhibitionist.scroller.description');
        miniatureClassname = 'exhibitionist-slider';
      } else {
        title = t('widgets.exhibitionist.gridGallery.addButtonHeader');
        description = t('widgets.exhibitionist.gridGallery.description');
        miniatureClassname = 'exhibitionist-grid';
      }
      break;
    case ModuleType.WATCHERS:
      title = t('widgets.watchers.addButtonHeader');
      description = t('widgets.watchers.description');
      miniatureClassname = 'watchers';
      break;
    case ModuleType.WATCHING:
      title = t('widgets.watching.addButtonHeader');
      description = t('widgets.watching.description');
      miniatureClassname = 'watching';
      break;
    case ModuleType.GROUPS_MEMBER:
      title = t('widgets.groups_member.addButtonHeader');
      description = t('widgets.groups_member.description');
      miniatureClassname = 'groups-member';
      break;
    case ModuleType.GROUPS_ADMIN:
      title = t('widgets.groups_admin.addButtonHeader');
      description = t('widgets.groups_admin.description');
      miniatureClassname = 'groups-admin';
      break;
    case ModuleType.DONATIONS:
      title = t('widgets.donations.addButtonHeader');
      description = t('widgets.donations.description');
      miniatureClassname = 'donations';
      break;
    case ModuleType.DEVIOUSNESS:
      title = t('widgets.deviousness.addButtonHeader');
      description = t('widgets.deviousness.description');
      miniatureClassname = 'deviousness';
      break;
    case ModuleType.FEED:
      title = t('widgets.feed.addButtonHeader');
      description = t('widgets.feed.description');
      miniatureClassname = 'feed';
      break;
    case ModuleType.MY_COMMENTS:
      title = t('widgets.myComments.addButtonHeader');
      description = t('widgets.myComments.description');
      miniatureClassname = 'my-comments';
      break;
    case ModuleType.COMMENTS:
      title = t('widgets.profileComments.addButtonHeader');
      description = t('widgets.profileComments.description');
      miniatureClassname = 'profile-comments';
      break;
    case ModuleType.BADGES_ACTIVITY:
      title = t('widgets.badges_activity.addButtonHeader');
      description = t('widgets.badges_activity.description');
      miniatureClassname = 'my-badges';
      break;
    case ModuleType.STATS:
      title = t('widgets.statistics.addButtonHeader');
      description = t('widgets.statistics.description');
      miniatureClassname = 'stats';
      break;
    case ModuleType.FEATURED_DEVIATION:
      title = t('widgets.featured_deviation.addButtonHeader');
      description = t('widgets.featured_deviation.description');
      miniatureClassname = 'featured-deviation';
      break;
    case ModuleType.FEATURED_POST:
      title = t('widgets.featured_post.addButtonHeader');
      description = t('widgets.featured_post.description');
      miniatureClassname = 'featured-post';
      break;
    case ModuleType.CUSTOM:
      title = t!('widgets.custombox.addButtonHeader');
      description = t!('widgets.custombox.description');
      miniatureClassname = 'custombox';
      rootClassname = s['bg-with-stars'];
      break;
    case ModuleType.MEGABOX:
      title = t!('widgets.megabox.addButtonHeader');
      description = t!('widgets.megabox.description');
      miniatureClassname = 'megabox';
      break;
    case ModuleType.TIERS:
      title = t!('widgets.tiers.addButtonHeader');
      description = t!('widgets.tiers.description');
      miniatureClassname = 'tiers';
      break;
    case ModuleType.BADGES_SPOTLIGHT:
      title = t!('widgets.badgesSpotlight.addButtonHeader');
      description = t!('widgets.badgesSpotlight.description');
      miniatureClassname = 'my-badges';
      break;
    default:
      title = moduleName;
      description = 'no widget type card';
  }

  return (
    <div className={classnames(s['root'], rootClassname, className)}>
      <div
        className={classnames(
          s['miniature'],
          s[`miniature-${miniatureClassname}`]
        )}
      />
      <div className={s['title']}>{title}</div>
      <div className={s['description']}>{description}</div>
      {isCoreOnlyModule && (
        <div className={s['core-exclusive']}>
          <CoreBadge size="large" className={s['core-badge']} />
          <span>{t('widgets.coreUpsell.exclusiveLabel')}</span>
        </div>
      )}
      {isLocked && (
        <div className={s['core-upsell-overlay']}>
          <div className={s['core-upsell-overlay-text']}>
            {t('widgets.coreUpsell.becomeCore.toUnlock', {
              postProcess: 'reactPostprocessor',
              count: coreLimit,
              coreBadge: <CoreBadge size="small" className={s['core-badge']} />,
              coreLimit,
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetTypeCard;
